// ─── Key Factory vs queryOptions ─────────────────────────────────────────────
//
// React Query v5 introduced queryOptions() as the recommended way to define
// queries. It co-locates queryKey + queryFn in one typed object, making
// per-query key factory entries redundant:
//
//   // ❌ Old pattern — key and queryFn defined separately, two places to sync
//   procureEstimateKeys.resources(builderId)
//   useQuery({ queryKey: procureEstimateKeys.resources(builderId), queryFn: ... })
//
//   // ✅ New pattern — one source of truth, access key via .queryKey
//   resourcesQueryOptions(builderId)
//   resourcesQueryOptions(builderId).queryKey  // use anywhere setQueryData/invalidate is needed
//
// For this reason, individual per-resource entries (resources, costCodes, etc.)
// are defined inline inside each queryOptions() call in useProcureAPI.ts, NOT here.
//
// This file only contains:
//   1. Base keys for broad prefix-based invalidation (e.g. invalidate all
//      procure data for a builder after a sync operation)
//   2. Keys that don't belong to a specific queryOptions factory (e.g.
//      studioDocVersions, which lives in a separate file)
//
// When adding a new query, define its key inline in queryOptions() and only
// add an entry here if you need prefix-based bulk invalidation for it.
// Check out useProcureAPI.ts for more details and examples.

// ─── Home plans & customers ──────────────────────────────────────────────────
export const HOME_PLANS_BASE_KEY = "home-plans";
export const CUSTOMERS_BASE_KEY = "customers";

export const homePlanKeys = {
  all: (builderId: string) => [HOME_PLANS_BASE_KEY, builderId] as const,
};

export const customerKeys = {
  all: (builderId: string) => [CUSTOMERS_BASE_KEY, builderId] as const,
};

export const HOME_PLAN_CONTRACTS_BASE_KEY = "home-plan-contracts";

export const homePlanContractKeys = {
  all: (builderId: string) => [HOME_PLAN_CONTRACTS_BASE_KEY, builderId] as const,
  byHomePlan: (builderId: string, homePlanId: string) =>
    [HOME_PLAN_CONTRACTS_BASE_KEY, builderId, "by-home-plan", homePlanId] as const,
};

// ─── Studio doc catalog options ──────────────────────────────────────────────
export const STUDIO_DOC_CATALOG_OPTIONS_BASE_KEY = "studio-doc-catalog-options";

export const studioDocCatalogOptionKeys = {
  // Matches the doc's own overlay and every doc ref's, which share this prefix.
  all: (builderId: string) => [STUDIO_DOC_CATALOG_OPTIONS_BASE_KEY, builderId] as const,

  // The doc's own and doc ref queryOptions factories key the same entry per doc, and both
  // handlePublish and updateDocRef invalidate it, so the key does not belong to either factory.
  byStudioDoc: (studioDocId: string, builderId: string) =>
    [STUDIO_DOC_CATALOG_OPTIONS_BASE_KEY, builderId, studioDocId] as const,
};

// ─── Procure & Estimate ──────────────────────────────────────────────────────
export const PROCURE_ESTIMATE_BASE_KEY = "procure-estimate";

export const procureEstimateKeys = {
  // Matches every procure-estimate query for a builder — use to invalidate all
  // procure data at once (e.g. after a plan sync or builder-level operation).
  all: (builderId: string) => [PROCURE_ESTIMATE_BASE_KEY, builderId] as const,

  // Defined here because it lives in useStudioDocVersions.ts, not useProcureAPI.ts.
  // All other per-resource keys are inlined in their queryOptions() definitions.
  // WE'll have to revisit and re-organize this later.
  studioDocVersions: (studioDocId: string, builderId: string) =>
    [PROCURE_ESTIMATE_BASE_KEY, "studioDocVersions", studioDocId, builderId] as const,
};
